import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, ShieldCheck, Smartphone, Copy, Trash2, LogOut, KeyRound } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

export function SettingsSecurityPage() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  // --- MFA state ---
  const [mfaSetup, setMfaSetup] = useState<{ secret: string; qrCodeDataUrl: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: async () => (await api.get('/api/auth/sessions')).data.sessions,
  });

  // MFA enabled status comes from /me — fetch fresh
  const profile = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => (await api.get('/api/auth/me')).data,
  });

  const mfaEnabled = profile.data?.user?.mfaEnabled ?? false;

  const startSetup = useMutation({
    mutationFn: async () => (await api.post('/api/auth/mfa/setup')).data,
    onSuccess: (data) => {
      setMfaSetup({ secret: data.secret, qrCodeDataUrl: data.qrCodeDataUrl });
    },
    onError: (err) => toast.error('Setup failed', extractApiError(err).message),
  });

  const verifySetup = useMutation({
    mutationFn: async () => (await api.post('/api/auth/mfa/verify', { code: mfaCode })).data,
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setMfaSetup(null);
      setMfaCode('');
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      toast.success('MFA enabled', 'Store your backup codes in a safe place');
    },
    onError: (err) => toast.error('Verification failed', extractApiError(err).message),
  });

  const disable = useMutation({
    mutationFn: async () =>
      api.post('/api/auth/mfa/disable', { password: disablePw, code: disableCode }),
    onSuccess: () => {
      toast.success('MFA disabled');
      setDisableOpen(false);
      setDisablePw('');
      setDisableCode('');
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err) => toast.error('Disable failed', extractApiError(err).message),
  });

  const regenerateCodes = useMutation({
    mutationFn: async () => (await api.post('/api/auth/mfa/backup-codes/regenerate')).data,
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      toast.success('Backup codes regenerated', 'Old codes are now invalid');
    },
    onError: (err) => toast.error('Failed', extractApiError(err).message),
  });

  const revokeSession = useMutation({
    mutationFn: async (id: string) => api.delete(`/api/auth/sessions/${id}`),
    onSuccess: () => {
      toast.success('Session revoked');
      qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (err) => toast.error('Failed', extractApiError(err).message),
  });

  const revokeOthers = useMutation({
    mutationFn: async () => (await api.post('/api/auth/sessions/revoke-others')).data,
    onSuccess: (data) => {
      toast.success('Other sessions revoked', `${data.revoked} session(s)`);
      qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (err) => toast.error('Failed', extractApiError(err).message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Security</h1>
        <p className="text-sm text-muted-foreground">
          Manage two-factor authentication, backup codes, and active sessions for{' '}
          <span className="font-medium">{me?.email}</span>.
        </p>
      </div>

      {/* MFA status card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${mfaEnabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-muted text-muted-foreground'}`}>
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>Two-factor authentication</CardTitle>
                <CardDescription>
                  {mfaEnabled ? 'MFA is active on your account' : 'Add an extra layer of security to your sign-in'}
                </CardDescription>
              </div>
            </div>
            {mfaEnabled ? (
              <Badge variant="success">
                <ShieldCheck className="mr-1 h-3 w-3" /> Enabled
              </Badge>
            ) : (
              <Badge variant="secondary">Disabled</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!mfaEnabled && !mfaSetup && (
            <Button onClick={() => startSetup.mutate()} disabled={startSetup.isPending}>
              <Smartphone className="mr-1.5 h-4 w-4" />
              {startSetup.isPending ? 'Starting...' : 'Set up authenticator app'}
            </Button>
          )}

          {mfaSetup && (
            <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
              <div className="grid gap-4 md:grid-cols-[auto,1fr]">
                <div className="flex h-40 w-40 items-center justify-center rounded-lg bg-white p-2">
                  <img src={mfaSetup.qrCodeDataUrl} alt="MFA QR code" className="h-full w-full" />
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">1. Scan the QR code</p>
                  <p className="text-muted-foreground text-xs">
                    Use Google Authenticator, Authy, 1Password, or any TOTP app.
                  </p>
                  <p className="font-medium mt-2">2. Or enter the secret manually</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-background px-2 py-1 text-xs font-mono">
                      {mfaSetup.secret}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(mfaSetup.secret);
                        toast.info('Copied', 'Secret copied to clipboard');
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mfa-code">3. Enter the 6-digit code</Label>
                <div className="flex gap-2">
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="max-w-[120px] text-center font-mono text-lg tracking-widest"
                    placeholder="123456"
                  />
                  <Button onClick={() => verifySetup.mutate()} disabled={mfaCode.length !== 6 || verifySetup.isPending}>
                    {verifySetup.isPending ? 'Verifying...' : 'Verify + enable'}
                  </Button>
                  <Button variant="outline" onClick={() => { setMfaSetup(null); setMfaCode(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {mfaEnabled && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => regenerateCodes.mutate()} disabled={regenerateCodes.isPending}>
                <KeyRound className="mr-1.5 h-4 w-4" /> Regenerate backup codes
              </Button>
              <Button variant="destructive" onClick={() => setDisableOpen(true)}>
                Disable MFA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Active sessions</CardTitle>
              <CardDescription>Devices currently signed in to your account.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => revokeOthers.mutate()} disabled={revokeOthers.isPending}>
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Revoke other sessions
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sessions.isLoading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {sessions.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">No active sessions.</p>
          )}
          <ul className="space-y-2">
            {sessions.data?.map((s: Session) => (
              <li key={s.id} className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="font-medium text-sm">{s.label ?? 'Unknown device'}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.ipAddress ?? 'Unknown IP'} · Last used {formatDate(s.lastUsedAt)} · Expires {formatDate(s.expiresAt)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => revokeSession.mutate(s.id)}
                  title="Revoke"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Backup codes modal (shown once after enable/regenerate) */}
      <Dialog open={!!backupCodes} onOpenChange={(o) => !o && setBackupCodes(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save your backup codes</DialogTitle>
            <DialogDescription>
              Each code works once. Store them somewhere safe — they&apos;re the only way to access your account if you lose your authenticator device.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {backupCodes?.map((c) => (
              <code key={c} className="rounded-md border bg-muted p-2 text-center font-mono text-sm">{c}</code>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(backupCodes?.join('\n') ?? '');
                toast.info('Copied', 'Backup codes copied');
              }}
            >
              <Copy className="mr-1.5 h-4 w-4" /> Copy all
            </Button>
            <Button onClick={() => setBackupCodes(null)}>I&apos;ve saved them</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable MFA modal */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription>
              Confirm your password and current MFA code to disable. Tenant policy may block this.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password</Label>
              <Input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} />
            </div>
            <div>
              <Label>6-digit MFA code</Label>
              <Input inputMode="numeric" maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))} className="font-mono tracking-widest" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => disable.mutate()} disabled={disable.isPending || !disablePw || disableCode.length !== 6}>
              {disable.isPending ? 'Disabling...' : 'Disable MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface Session {
  id: string;
  label: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  expiresAt: string;
}
